import { useAuth0 } from '@auth0/auth0-react';
import React, { useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import ProtectedRoute from './auth/ProtectedRoute';
import RootLayout from './layouts/RootLayout';
import CallbackPage from './pages/CallbackPage/CallbackPage';
import Kindergartens from './pages/Kindergartens/Kindergartens';
import NotFoundPage from './pages/NotFoundPage/NotFoundPage';
import UnauthorizedPage from './pages/UnauthorizedPage/UnathorizedPage';
import { setTokenGetter } from './api/api';
import {
  CALLBACK,
  CHILD_CREATE,
  CHILD_DETAILS,
  CHILD_EDIT,
  GROUP_DETAILS,
  INFOBOARD,
  KINDERGARTEN_DETAILS,
  KINDERGARTENS,
  LOCATIONS_CREATE,
  TEACHER_DETAILS,
  TEACHER_EDIT,
  TEACHERS_CREATE,
  UNATHORIZED,
  PARENT_CREATE,
  PARENT_EDIT,
  PARENT_DETAILS,
  POST_CREATE,
  POST_EDIT,
  EVENT_CREATE,
  EVENT_DETAILS
} from './common/constants/routes.constants';
import Infoboard from './pages/Infoboard/Infoboard';
import { useUserStore } from './store/useUserStore';
import { getUserAdministrationInfo } from './api/auth.api';
import KindergartenDetails from './pages/KindergartenDetails/KindergartenDetails';
import CreateLocation from './pages/CreateLocation/CreateLocation';
import { ToastContainer } from 'react-toastify';
import GroupDetails from './pages/GroupDetails/GroupDetails';
import TeacherPage from './pages/KindergartenDetails/components/TeacherPage';
import { ActionMode } from './common/enums/common.enum';
import ChildPage from './pages/KindergartenDetails/components/ChildPage';
import ParentPage from './pages/KindergartenDetails/components/ParentPage';
import PostPage from './pages/Infoboard/components/PostPage/PostPage';
import ScreenSizeWarning from './components/ScreenSizeWarning/ScreenSizeWarning';
import CreateConfiguration from './pages/KindergartenDetails/components/EventTab/CreateConfiguration/CreateConfiguration';
import GroupEventConfiguration from './pages/KindergartenDetails/components/GroupEventConfiguration';

function App() {
  const { isLoading, isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();

  const { setUserInfo, userInfo, clear } = useUserStore();

  useEffect(() => {
    setTokenGetter(() => getAccessTokenSilently());
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (isAuthenticated && !userInfo) {
      fetchUserInfo();
    }
  }, [isAuthenticated, userInfo, setUserInfo]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !window.location.pathname.startsWith(CALLBACK)) {
      clear();
      loginWithRedirect();
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  const fetchUserInfo = async () => {
    try {
      const res = await getUserAdministrationInfo();
      setUserInfo(res);
    } catch (err) {
      console.error('Failed to fetch user info');
    }
  };

  if (isLoading || (!isAuthenticated && !window.location.pathname.startsWith(CALLBACK))) {
    return null;
  }

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
      <ScreenSizeWarning />
      <Routes>
        <Route path="/" element={<RootLayout />}>
          <Route path={CALLBACK} element={<CallbackPage />} />
          <Route
            path={KINDERGARTENS}
            element={
              <ProtectedRoute>
                <Kindergartens />
              </ProtectedRoute>
            }
          />
          <Route
            path={KINDERGARTEN_DETAILS}
            element={
              <ProtectedRoute>
                <KindergartenDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path={LOCATIONS_CREATE}
            element={
              <ProtectedRoute>
                <CreateLocation />
              </ProtectedRoute>
            }
          />
          <Route
            path={TEACHERS_CREATE}
            element={
              <ProtectedRoute>
                <TeacherPage mode={ActionMode.CREATE} />
              </ProtectedRoute>
            }
          />
          <Route
            path={TEACHER_EDIT}
            element={
              <ProtectedRoute>
                <TeacherPage mode={ActionMode.EDIT} />
              </ProtectedRoute>
            }
          />
          <Route
            path={TEACHER_DETAILS}
            element={
              <ProtectedRoute>
                <TeacherPage mode={ActionMode.DETAILS} />
              </ProtectedRoute>
            }
          />
          <Route
            path={CHILD_CREATE}
            element={
              <ProtectedRoute>
                <ChildPage mode={ActionMode.CREATE} />
              </ProtectedRoute>
            }
          />
          <Route
            path={CHILD_EDIT}
            element={
              <ProtectedRoute>
                <ChildPage mode={ActionMode.EDIT} />
              </ProtectedRoute>
            }
          />
          <Route
            path={CHILD_DETAILS}
            element={
              <ProtectedRoute>
                <ChildPage mode={ActionMode.DETAILS} />
              </ProtectedRoute>
            }
          />
          <Route
            path={PARENT_CREATE}
            element={
              <ProtectedRoute>
                <ParentPage mode={ActionMode.CREATE} />
              </ProtectedRoute>
            }
          />
          <Route
            path={PARENT_EDIT}
            element={
              <ProtectedRoute>
                <ParentPage mode={ActionMode.EDIT} />
              </ProtectedRoute>
            }
          />
          <Route
            path={PARENT_DETAILS}
            element={
              <ProtectedRoute>
                <ParentPage mode={ActionMode.DETAILS} />
              </ProtectedRoute>
            }
          />
          <Route
            path={GROUP_DETAILS}
            element={
              <ProtectedRoute>
                <GroupDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path={INFOBOARD}
            element={
              <ProtectedRoute>
                <Infoboard />
              </ProtectedRoute>
            }
          />
          <Route
            path={POST_CREATE}
            element={
              <ProtectedRoute>
                <PostPage mode={ActionMode.CREATE} />
              </ProtectedRoute>
            }
          />
          <Route
            path={POST_EDIT}
            element={
              <ProtectedRoute>
                <PostPage mode={ActionMode.EDIT} />
              </ProtectedRoute>
            }
          />
          <Route path={UNATHORIZED} element={<UnauthorizedPage />} />
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <NotFoundPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={EVENT_CREATE}
            element={
              <ProtectedRoute>
                <CreateConfiguration />
              </ProtectedRoute>
            }
          />
          <Route
            path={EVENT_DETAILS}
            element={
              <ProtectedRoute>
                <GroupEventConfiguration />
              </ProtectedRoute>
            }
          />
        </Route>
      </Routes>
    </>
  );
}

export default App;
